import React, { useEffect, useState, useMemo } from "react";
import { api, BRAND, formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/Common";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Check, X, FileText, MessageCircle, Settings2 } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { whatsappLink, renderWhatsappMessage, computePaymentDisplayStatus,
  STATUS_LABEL, STATUS_CHIP, DEFAULT_WHATSAPP_TEMPLATE,
} from "@/lib/whatsapp";
import { registeredByMonth } from "@/lib/eligibility";
import FinanceReport from "./FinanceReport";

const CATEGORIES = ["Sub-12", "Sub-14", "Sub-16", "Sub-20", "Adulto"];

function currentMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export default function Finance() {
  const [month, setMonth] = useState(currentMonth());
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [payments, setPayments] = useState([]);
  const [amounts, setAmounts] = useState({});
  const [reportOpen, setReportOpen] = useState(false);
  const [filterClass, setFilterClass] = useState("__all__");
  const [filterCat, setFilterCat] = useState("__all__");
  const [waTemplate, setWaTemplate] = useState(DEFAULT_WHATSAPP_TEMPLATE);
  const [waEditOpen, setWaEditOpen] = useState(false);
  const [waDraft, setWaDraft] = useState("");
  const [savingTpl, setSavingTpl] = useState(false);

  const load = async () => {
    try {
      const [s, p, c, tpl] = await Promise.all([
        api.get("/students"),
        api.get("/payments", { params: { month } }),
        api.get("/classes"),
        api.get("/settings/whatsapp-template"),
      ]);
      const active = s.data.filter(x => x.status === "ativo");
      setStudents(active);
      setClasses(c.data);
      setPayments(p.data);
      setWaTemplate(tpl.data.template || DEFAULT_WHATSAPP_TEMPLATE);
      const a = {};
      for (const st of active) {
        const pay = p.data.find(x => x.student_id === st.id);
        if (pay && pay.amount != null) a[st.id] = pay.amount;
        else if (st.monthly_fee != null) a[st.id] = st.monthly_fee;
        else a[st.id] = "";
      }
      setAmounts(a);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month]);

  const byStudent = useMemo(() => Object.fromEntries(payments.map(p => [p.student_id, p])), [payments]);

  const filteredStudents = useMemo(() => {
    return students
      .filter(s => registeredByMonth(s, month))
      .filter(s => filterClass === "__all__" || (filterClass === "__none__" ? !s.class_id : s.class_id === filterClass))
      .filter(s => filterCat === "__all__" || s.category === filterCat)
      .sort((a, b) => a.name.localeCompare(b.name, "pt-BR"));
  }, [students, filterClass, filterCat, month]);

  const setStatus = async (studentId, status) => {
    try {
      await api.post("/payments", {
        student_id: studentId, month, status,
        amount: amounts[studentId] ? Number(amounts[studentId]) : null,
      });
      toast.success("Atualizado");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const openWhatsApp = (s, displayStatus) => {
    const phone = s.guardian_phone || s.phone;
    if (!phone) { toast.error("Aluno não tem telefone do responsável cadastrado"); return; }
    const situation = displayStatus === "atrasado" ? "atrasada" : "a vencer";
    const msg = renderWhatsappMessage(waTemplate, {
      studentName: s.name, guardianName: s.guardian || "",
      month, amount: amounts[s.id] || s.monthly_fee, situation,
    });
    const url = whatsappLink(phone, msg);
    if (!url) { toast.error("Telefone inválido"); return; }
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const saveTemplate = async () => {
    if (!waDraft.trim()) { toast.error("Mensagem não pode ficar vazia"); return; }
    setSavingTpl(true);
    try {
      const { data } = await api.put("/settings/whatsapp-template", { template: waDraft.trim() });
      setWaTemplate(data.template);
      toast.success("Mensagem salva");
      setWaEditOpen(false);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSavingTpl(false); }
  };

  const counts = useMemo(() => {
    const c = { pago: 0, a_vencer: 0, atrasado: 0 };
    for (const s of filteredStudents) {
      const p = byStudent[s.id];
      const ds = computePaymentDisplayStatus({ status: p?.status, month });
      c[ds]++;
    }
    return c;
  }, [filteredStudents, byStudent, month]);

  return (
    <div data-testid="admin-finance-page">
      <PageHeader eyebrow="Financeiro" title="Mensalidades" subtitle="Vencimento todo dia 10. Pago / A vencer / Atrasado calculados automaticamente." mascot={BRAND.MANGUITO} />

      <div className="vv-card p-4 mb-4 flex flex-col sm:flex-row gap-3 sm:items-end flex-wrap">
        <div>
          <Label>Mês</Label>
          <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="w-48" data-testid="finance-month-input" />
        </div>
        <div>
          <Label>Turma</Label>
          <Select value={filterClass} onValueChange={setFilterClass}>
            <SelectTrigger className="w-48 bg-background/60" data-testid="finance-filter-class"><SelectValue placeholder="Turma" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Todas as turmas</SelectItem>
              <SelectItem value="__none__">Sem turma</SelectItem>
              {classes.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Categoria</Label>
          <Select value={filterCat} onValueChange={setFilterCat}>
            <SelectTrigger className="w-44 bg-background/60" data-testid="finance-filter-category"><SelectValue placeholder="Categoria" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Todas as categorias</SelectItem>
              {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Button
          variant="outline" onClick={() => setReportOpen(true)} data-testid="open-report-button"
          className="border-[hsl(var(--vv-lime))/0.5] text-[hsl(var(--vv-lime))] hover:bg-[hsl(var(--vv-lime))] hover:text-[hsl(218_50%_10%)] uppercase tracking-wider font-bold"
        >
          <FileText className="size-4 mr-2" /> Relatório
        </Button>
        <Button
          variant="outline"
          onClick={() => { setWaDraft(waTemplate); setWaEditOpen(true); }}
          data-testid="edit-whatsapp-template-button"
          className="border-[#25D366]/60 text-[#25D366] hover:bg-[#25D366] hover:text-white uppercase tracking-wider font-bold"
        >
          <Settings2 className="size-4 mr-2" /> Mensagem WhatsApp
        </Button>
        <div className="flex gap-2 ml-auto flex-wrap">
          <div className="vv-stat px-4 py-2">
            <div className="text-[10px] uppercase text-muted-foreground">Pagos</div>
            <div className="text-xl font-black text-[hsl(var(--vv-lime))]">{counts.pago}</div>
          </div>
          <div className="vv-stat px-4 py-2">
            <div className="text-[10px] uppercase text-muted-foreground">A vencer</div>
            <div className="text-xl font-black text-[hsl(var(--vv-yellow))]">{counts.a_vencer}</div>
          </div>
          <div className="vv-stat px-4 py-2">
            <div className="text-[10px] uppercase text-muted-foreground">Atrasados</div>
            <div className="text-xl font-black text-[hsl(0_80%_70%)]">{counts.atrasado}</div>
          </div>
        </div>
      </div>

      <div className="vv-card overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="uppercase text-xs">
              <TableHead>Aluno</TableHead>
              <TableHead>Categoria</TableHead>
              <TableHead>Valor (R$)</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Ações</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredStudents.map((s) => {
              const p = byStudent[s.id];
              const ds = computePaymentDisplayStatus({ status: p?.status, month });
              const showWa = ds !== "pago" && (s.guardian_phone || s.phone);
              return (
                <TableRow key={s.id} data-testid={`finance-row-${s.id}`}>
                  <TableCell className="font-semibold">{s.name}</TableCell>
                  <TableCell><span className="vv-chip vv-chip-blue">{s.category}</span></TableCell>
                  <TableCell>
                    <Input
                      type="number" step="0.01" min={0}
                      value={amounts[s.id] ?? ""}
                      onChange={(e) => setAmounts(a => ({ ...a, [s.id]: e.target.value }))}
                      placeholder="0,00" className="w-28"
                    />
                  </TableCell>
                  <TableCell>
                    <span className={`vv-chip ${STATUS_CHIP[ds]}`} data-testid={`finance-status-${s.id}`}>
                      {STATUS_LABEL[ds]}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2 flex-wrap">
                      {showWa && (
                        <Button
                          size="sm" variant="outline"
                          onClick={() => openWhatsApp(s, ds)}
                          data-testid={`whatsapp-${s.id}`}
                          className="border-[#25D366]/60 text-[#25D366] hover:bg-[#25D366] hover:text-white"
                          title="Cobrar via WhatsApp"
                        >
                          <MessageCircle className="size-4 mr-1" /> WhatsApp
                        </Button>
                      )}
                      <Button size="sm" variant="outline" onClick={() => setStatus(s.id, "pago")} className="border-[hsl(var(--vv-lime))/0.5] text-[hsl(var(--vv-lime))] hover:bg-[hsl(var(--vv-lime))] hover:text-[hsl(218_50%_10%)]" data-testid={`mark-paid-${s.id}`}>
                        <Check className="size-4 mr-1" /> Pago
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => setStatus(s.id, "pendente")} data-testid={`mark-pending-${s.id}`}>
                        <X className="size-4 mr-1" /> Pendente
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <FinanceReport open={reportOpen} onOpenChange={setReportOpen} initialMonth={month} />

      <Dialog open={waEditOpen} onOpenChange={setWaEditOpen}>
        <DialogContent className="max-w-xl" data-testid="whatsapp-template-dialog">
          <DialogHeader>
            <DialogTitle>Editar mensagem do WhatsApp</DialogTitle>
            <DialogDescription>
              Esta é a mensagem padrão enviada ao clicar em "WhatsApp" no financeiro. Use as variáveis abaixo:
            </DialogDescription>
          </DialogHeader>
          <div className="text-xs text-muted-foreground mb-2">
            <code>{"{nome}"}</code> — nome do aluno • <code>{"{responsavel}"}</code> — nome do responsável • <code>{"{mes}"}</code> — mês/ano • <code>{"{valor}"}</code> — valor em R$ • <code>{"{situacao}"}</code> — "a vencer" ou "atrasada"
          </div>
          <Textarea
            value={waDraft}
            onChange={(e) => setWaDraft(e.target.value)}
            rows={8}
            className="font-mono text-sm"
            data-testid="whatsapp-template-textarea"
          />
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setWaDraft(DEFAULT_WHATSAPP_TEMPLATE)} data-testid="whatsapp-template-reset">
              Restaurar padrão
            </Button>
            <Button variant="ghost" onClick={() => setWaEditOpen(false)}>Cancelar</Button>
            <Button onClick={saveTemplate} disabled={savingTpl} data-testid="whatsapp-template-save" className="bg-[#25D366] text-white hover:bg-[#1ebe5d] uppercase tracking-wider font-bold">
              {savingTpl ? "Salvando..." : "Salvar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
